import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Download, Share2, FileDown, Box, PanelLeft, PanelRight,
  Sun, Moon, MoreVertical, Github, Info,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useTheme } from "./theme/ThemeProvider";

function ToggleBtn({ active, onClick, testId, title, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      title={title}
      className={`h-9 w-9 flex items-center justify-center rounded-none transition-colors ${
        active
          ? "bg-[#002FA7] text-white"
          : "text-ink3 hover:bg-hover hover:text-ink"
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export default function EditorHeader({
  objectCount,
  onDownloadSource,
  onShare,
  onExportSvg,
  onExportZip,
  leftOpen,
  rightOpen,
  toggleLeft,
  toggleRight,
  isCompact,
}) {
  const { theme, toggleTheme } = useTheme();
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <>
    <header className="floating-panel h-14 flex items-center justify-between px-3 sm:px-4 z-30 shrink-0 gap-2">
      {/* LEFT: brand + panel toggles */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center gap-2 shrink-0">
          {/* <Box className="h-4 w-4 text-[#FFB400]" /> */}
          <img src="./logo.png" alt="Logo" className="h-4 w-4" />
          <span className="font-mono text-sm tracking-tighter font-semibold text-ink hidden sm:inline">SVG STUDIO</span>
          <span className="hidden md:inline text-[10px] text-ink4 font-mono uppercase tracking-widest ml-1">v0.2</span>
        </div>
        <div className="h-6 w-px bg-line mx-1" />
        <div className="flex items-center border border-line">
          <ToggleBtn active={leftOpen} onClick={toggleLeft} testId="toggle-left-panel-btn" title="Library 面板" icon={PanelLeft} />
          <div className="h-9 w-px bg-line" />
          <ToggleBtn active={rightOpen} onClick={toggleRight} testId="toggle-right-panel-btn" title="Inspector 面板" icon={PanelRight} />
        </div>
      </div>

      {/* RIGHT: obj count + theme + actions */}
      <div className="flex items-center gap-2">
        <span className="hidden lg:inline text-[10px] font-mono uppercase tracking-widest text-ink4 mr-1">
          {objectCount} OBJ
        </span>

        <ToggleBtn
          active={false}
          onClick={() => setInfoOpen(true)}
          testId="info-btn"
          title="使用说明 / Help"
          icon={Info}
        />

        <a
          href="https://github.com/cgbin24"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="github-link-btn"
          title="GitHub"
          className="h-9 w-9 flex items-center justify-center rounded-none text-ink3 hover:bg-hover hover:text-ink transition-colors"
        >
          <Github className="h-4 w-4" />
        </a>

        <ToggleBtn
          active={false}
          onClick={toggleTheme}
          testId="theme-toggle-btn"
          title={theme === "dark" ? "切换亮色 / Light" : "切换暗色 / Dark"}
          icon={theme === "dark" ? Sun : Moon}
        />

        <div className="h-6 w-px bg-line mx-1" />

        {isCompact ? (
          <>
            <Button
              onClick={onExportZip}
              data-testid="export-zip-btn"
              className="rounded-none h-9 bg-[#002FA7] hover:bg-[#0038C7] text-white gap-2 font-mono text-xs uppercase tracking-wider px-3"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">ZIP</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-more-menu-btn"
                  className="h-9 w-9 flex items-center justify-center rounded-none text-ink3 hover:bg-hover hover:text-ink transition-colors border border-line"
                  title="More"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none font-mono text-xs uppercase tracking-wider">
                {/* <DropdownMenuItem data-testid="menu-source-item" onClick={onDownloadSource} className="gap-2 cursor-pointer">
                  <Download className="h-3.5 w-3.5" /> Source
                </DropdownMenuItem> */}
                <DropdownMenuItem data-testid="menu-share-item" onClick={onShare} className="gap-2 cursor-pointer">
                  <Share2 className="h-3.5 w-3.5" /> Share
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-export-svg-item" onClick={onExportSvg} className="gap-2 cursor-pointer">
                  <FileDown className="h-3.5 w-3.5" /> Export SVG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <>
            {/* <Button
              onClick={onDownloadSource}
              data-testid="download-source-btn"
              variant="ghost"
              className="rounded-none h-9 text-ink2 hover:bg-hover hover:text-ink px-3 gap-2 font-mono text-xs uppercase tracking-wider"
            >
              <Download className="h-3.5 w-3.5" />
              Source
            </Button> */}
            <Button
              onClick={onShare}
              variant="ghost"
              data-testid="share-btn"
              className="rounded-none h-9 text-ink2 hover:bg-hover hover:text-ink gap-2 font-mono text-xs uppercase tracking-wider"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share
            </Button>
            <Button
              onClick={onExportSvg}
              variant="outline"
              data-testid="export-svg-btn"
              className="rounded-none h-9 bg-transparent border-line text-ink2 hover:bg-hover hover:text-ink gap-2 font-mono text-xs uppercase tracking-wider"
            >
              <FileDown className="h-3.5 w-3.5" />
              Export SVG
            </Button>
            <Button
              onClick={onExportZip}
              data-testid="export-zip-btn"
              className="rounded-none h-9 bg-[#002FA7] hover:bg-[#0038C7] text-white gap-2 font-mono text-xs uppercase tracking-wider"
            >
              <Download className="h-3.5 w-3.5" />
              Export ZIP
            </Button>
          </>
        )}
      </div>
    </header>

    <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
      <DialogContent className="rounded-none max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono tracking-tight">使用说明 · SVG Studio</DialogTitle>
          <DialogDescription className="text-ink3">
            以下是当前可用的核心交互与功能，随着版本迭代会持续补充。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-ink2 leading-relaxed pt-2">
          <section>
            <h4 className="font-mono text-xs uppercase tracking-widest text-ink4 mb-1.5">基础操作</h4>
            <ul className="space-y-1 list-disc pl-4">
              <li>左侧面板选择基础图形、上传图片或套用模板，点击即可添加到画布。</li>
              <li>单击图形/图片：选中并将其层级置顶显示。</li>
              <li>拖动图形：移动位置（支持多选后整体移动）。</li>
              <li>按住 <kbd className="px-1 py-0.5 bg-hover rounded text-xs">Option/Alt</kbd> 后拖动：复制选中的图形/图片。</li>
              <li>按住 <kbd className="px-1 py-0.5 bg-hover rounded text-xs">Shift</kbd> 或 <kbd className="px-1 py-0.5 bg-hover rounded text-xs">⌘/Ctrl</kbd> 单击：加选/减选多个元素。</li>
              <li>在空白处拖动：框选元素。</li>
            </ul>
          </section>

          <section>
            <h4 className="font-mono text-xs uppercase tracking-widest text-ink4 mb-1.5">图片裁切</h4>
            <ul className="space-y-1 list-disc pl-4">
              <li>按住 <kbd className="px-1 py-0.5 bg-hover rounded text-xs">Option/Alt</kbd> 双击图片：进入裁切模式。</li>
              <li>拖动裁切框或四角/四边手柄调整裁切区域，裁切结果会“烘焙”进外包围框。</li>
              <li>双击空白处：退出并保存裁切；再次进入可基于原始图片重新调整。</li>
              <li>右侧 Inspector 可单独设置圆角，与裁切组合使用。</li>
            </ul>
          </section>

          <section>
            <h4 className="font-mono text-xs uppercase tracking-widest text-ink4 mb-1.5">画布与视图</h4>
            <ul className="space-y-1 list-disc pl-4">
              <li>滚轮：平移画布；<kbd className="px-1 py-0.5 bg-hover rounded text-xs">⌘/Ctrl</kbd> + 滚轮：缩放。</li>
              <li>按住 <kbd className="px-1 py-0.5 bg-hover rounded text-xs">Space</kbd> 拖动：平移画布。</li>
              <li>顶部 Header 可切换左右面板、主题与导出。</li>
            </ul>
          </section>

          <section>
            <h4 className="font-mono text-xs uppercase tracking-widest text-ink4 mb-1.5">动画与导出</h4>
            <ul className="space-y-1 list-disc pl-4">
              <li>左侧 Animations 面板为选中元素应用入场/循环动画。</li>
              <li>顶部 Export SVG 导出单文件；Export ZIP 导出含动画的完整工程包。</li>
              <li>Share 生成可分享的链接（数据内嵌于 URL）。</li>
            </ul>
          </section>

          <section>
            <h4 className="font-mono text-xs uppercase tracking-widest text-ink4 mb-1.5">编辑与历史</h4>
            <ul className="space-y-1 list-disc pl-4">
              <li><kbd className="px-1 py-0.5 bg-hover rounded text-xs">⌘/Ctrl</kbd> + Z 撤销，<kbd className="px-1 py-0.5 bg-hover rounded text-xs">⌘/Ctrl</kbd> + Shift + Z 重做。</li>
              <li><kbd className="px-1 py-0.5 bg-hover rounded text-xs">⌘/Ctrl</kbd> + D 复制选中元素。</li>
              <li>双击可编辑的线条：在最近线段中点插入折点。</li>
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
